/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   BRISK — COLLECTION INFINITE SCROLL
   Loaded deferred on the collection template only. Rebuilt from nothing on 2026-08-04; the two
   previous attempts on this theme are documented in layout/theme.liquid. Read that before editing.

   ── HOW THIS IS FAST, WHICH IS THE WHOLE DESIGN ──────────────────────────────────────────────
   Measured on the dev theme: rendering the next page SERVER-SIDE costs ~1,357 ms warm and ~224 KB.
   That number cannot be optimised away from here — it is Shopify rendering Liquid, and the network is
   not the bottleneck. So it is never on the critical path:

       ALWAYS EXACTLY ONE PAGE AHEAD.
       Page 2 is fetched and parsed during idle time after first paint, and held as a ready
       DocumentFragment. When the sentinel fires, the fragment is already in memory: the shopper's wait
       is one appendChild, not a page render. The moment it is inserted, page 3 starts fetching.

   The alternative — fetching when the sentinel fires — cannot work here. A page of products is about
   1,360 px tall at 5-up, and a fast scroll crosses that in well under 1,357 ms, so the shopper would
   arrive before the server did. Buffering one page ahead gives the render a full page of runway.

   ── WHY SECTION RENDERING AND NOT A LIGHTER ENDPOINT ─────────────────────────────────────────
   🚨 `product-block` reads TEN values off `section.settings`, including `discountValue` and
   `discountText` — the MULTIBUY tag. An alternate template rendered without a layout would be a much
   smaller payload, but `section` does not exist there, so every appended card would silently lose its
   badge. That was built, caught against templates/collection.json (`discountValue: 20`), and deleted.
   The cost of correctness is that ~105 KB of each response is chrome we discard. Accepted, and it is
   prefetched at low priority. See me/PERFORMANCE.md.

   ── THE TRAPS, ALL LEARNED THE HARD WAY ON THIS THEME ────────────────────────────────────────
   🚨 APPENDED CARDS MUST GET `theme.ProductBlockManager.loadImages()`. It is what strips
      `.lazyload--manual`; without it page 2 renders with no images at all.
   🚨 `cc:component:load` IS FIRED WITH jQUERY `.trigger()`. A native addEventListener cannot hear it.
      The filter-reset hook below must stay jQuery-bound.
   🚨 A FETCH IN FLIGHT MUST BE ABORTED WHEN THE FILTERS CHANGE, or a response for the previous filter
      lands on top of the new results and appends products that do not match.
   🚨 EAGER IMAGES ARE STRIPPED CLIENT-SIDE. The server renders every page's first 8 cards eager with
      one `fetchpriority="high"`, which is right for someone landing on ?page=3 and wrong for an
      append. Liquid cannot tell the two apart. A DOMParser document has NO BROWSING CONTEXT, so no
      image request has started when we rewrite the attributes — this is why it must happen before
      insertion, on the parsed fragment, and not after.
   ═══════════════════════════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var root = document.querySelector('[data-bc-inf]');
  if (!root) return;

  var grid = document.querySelector('[data-product-grid]');
  if (!grid) return;

  var sentinel = root.querySelector('[data-bc-sentinel]');
  var spinner = root.querySelector('[data-bc-spinner]');
  /* 🚨 `data-bc-loadmore`, NOT `data-bc-more`. `data-bc-more` already belongs to the filter bar's
     "More filters" toggle, and this block renders INSIDE `.filter-container--bar`, so
     brisk-collection.js's capture-phase handler matched this button as that toggle and called
     stopPropagation() on the click — the listener below never ran and Load more did nothing.
     Two features, two attribute names. */
  var moreBtn = root.querySelector('[data-bc-loadmore]');
  var status = root.querySelector('[data-bc-status]');

  var sectionId = root.dataset.bcSection;
  /* 2026-08-28: appended pages come from sections/collection-grid-page.liquid — cards + cursor only,
     none of the filter-bar / header / pagination chrome that main-collection renders and we discard
     (~52 KB and the 9-facet aggregation per request, all counted against Shopify's render budget).
     Advertised by main-collection as data-bc-grid-section; absent (search) → the page's own section.
     A 404 (the file missing on the theme being tested) falls back to the page's section for good. */
  var gridSection = root.dataset.bcGridSection || '';
  var gridBroken = false;
  var perPage = parseInt(root.dataset.bcPerPage, 10) || 0;
  var autoPages = parseInt(root.dataset.bcAutoPages, 10);
  if (isNaN(autoPages)) autoPages = 4;

  var nextUrl = root.dataset.bcNext || '';
  var buffer = null; /* a parsed, ready DocumentFragment for `nextUrl` */
  var bufferedFor = null; /* the url `buffer` belongs to, so a stale buffer can never be used */
  var inFlight = null; /* AbortController */
  var inFlightFor = null; /* the url `inFlight` is fetching, so advance() can ride it instead of
                             aborting and re-rendering the identical page (QA 2026-08-07 found pages
                             3 and 4 each rendered TWICE by Shopify under fast scrolling) */
  var autoUsed = 0;
  var suspended = false; /* a filter/sort change is mid-flight; stop SPECULATING until reset().
                            2026-08-07: the prefetch used to keep transferring for the OLD filter
                            state for the entire duration of the shopper's filter fetch — it only
                            died at reset(), which fires AFTER the grid swap. `priority:'low'` is a
                            hint, not a guarantee, so on narrow links the two competed head-on.
                            Suspension gates prefetch() and its failure-recovery only — the
                            on-demand sentinel/button path stays live, so even if reset() never
                            arrives (failed filter fetch) infinite scroll degrades gracefully
                            instead of deadlocking. */
  var rideActive = false; /* advance() is waiting on a prefetch it decided to ride — see advance() */
  var seen = {}; /* page numbers already appended — the last line of defence against duplicates */
  var total = parseInt(root.dataset.bcTotal, 10) || 0; /* collection.products_count, for the poison check */
  var poisoned = {}; /* page -> how many poisoned renders we have refused for it */
  var retries = 0;
  var MAX_POISON_RETRIES = 2;
  var paused = false; /* Load more is showing: the observer stays attached but crossings are ignored */
  var outcome = ''; /* last abnormal outcome, mirrored to data-bc-outcome for RUM (see mark()) */
  var reported = {}; /* outcome kinds already surfaced as an uncaught error this page view */
  var firstPrefetchAt = 0, firstPrefetchMs = 0;
  var observer = null;
  var spinnerTimer = null;
  var disabled = false;

  /* ── Utilities ─────────────────────────────────────────────────────────────────────────────── */

  /* The fetch URL is always derived from the server's own `paginate.next.url`, which already carries
     the active filters and sort. We only add `section_id`, and `searchParams.set` guarantees it cannot
     accumulate across pages the way string concatenation would. */
  /* 🚨 A RETRY MUST BYPASS SHOPIFY'S PAGE CACHE (2026-08-28). Shopify's page cache stores a
     render that died with `Liquid error: internal` as a normal 200 and replays it; several
     copies per URL exist and some are poisoned (measured: 3 of 9 cache hits on one URL were the
     error). Unknown query params (`bcr=`, `bcfresh=`, `_x=`) are NOT part of the key — those
     retries re-fetched the same poisoned copy at ~25 ms. `view=` IS part of the key, and an
     unknown view suffix falls back to the default template (measured on BOTH themes: 200, fresh
     render;dur, identical 16 cards) — so a unique `view=` value guarantees a fresh render.
     ⛔ Not the bulk `sections=` endpoint: it is page-cached exactly like `section_id=`
     (measured: 28 ms cache hits on the second call). ⚠ A 404 here means the SECTION ID is
     wrong for the theme being tested (ids are per theme), not that `view=` failed. */
  function renderUrl(url, attempt) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', (gridSection && !gridBroken) ? gridSection : sectionId);
    if (attempt) u.searchParams.set('view', 'bcr' + Date.now().toString(36) + attempt);
    return u.toString();
  }

  /* The bulk endpoint answers JSON {"<section id>": "<html>"}; the single endpoint answers HTML.
     Normalise so parse() always sees the section's HTML. */
  function sectionHtml(text) {
    if (text && text.charAt(0) === '{') {
      try {
        var j = JSON.parse(text);
        var k = Object.keys(j)[0];
        if (k && typeof j[k] === 'string') return j[k];
      } catch (e) { /* fall through: treat as HTML */ }
    }
    return text;
  }

  /* `paginate.next.url` echoes the request's own query string, so after a retry it carries our
     `view=bcr…` key. Strip it: the cursor must stay the canonical URL or every later page would
     be an uncached render under a stale key (seen in QA 2026-08-28: p4..p8 all "+retry"). */
  function cleanNext(next) {
    if (!next) return '';
    try {
      var u = new URL(next, window.location.origin);
      var v = u.searchParams.get('view');
      if (v && v.indexOf('bcr') === 0) u.searchParams.delete('view');
      return u.pathname + u.search;
    } catch (e) {
      return next;
    }
  }

  function pageOf(url) {
    try {
      return parseInt(new URL(url, window.location.origin).searchParams.get('page'), 10) || null;
    } catch (e) {
      return null;
    }
  }

  /* Slow or metered connections do not get speculative work: prefetching a page the shopper may never
     reach is a good trade on wifi and a bad one on a metered 2g link. They still get infinite scroll,
     just fetched on demand. */
  function isFrugal() {
    var c = navigator.connection;
    if (!c) return false;
    return !!c.saveData || c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
  }

  var idle =
    window.requestIdleCallback ||
    function (cb) {
      return setTimeout(function () {
        cb({ timeRemaining: function () { return 50; } });
      }, 1);
    };

  /* ── Observability ──────────────────────────────────────────────────────────────────────────
     Every abnormal exit writes data-bc-outcome on the root and logs a stable `[brisk-inf] <kind>
     page=… have=… total=… retries=…` line. For the two kinds a shopper can actually lose products
     to (poisoned, short-end) it ALSO throws one synthetic uncaught Error per page view, so
     RenderBetter's js_errors subject sees it in the field without a custom RUM hook. Bounded to one
     per kind per page view; never thrown synchronously in our own call stack. */
  function mark(kind, extra) {
    outcome = kind;
    var have = grid ? grid.querySelectorAll('.product-block').length : -1;
    var msg = '[brisk-inf] ' + kind + ' page=' + (extra && extra.page != null ? extra.page : '?') +
      ' have=' + have + ' total=' + total + ' retries=' + retries;
    if (root) root.setAttribute('data-bc-outcome', kind);
    if (status && extra && extra.text) status.textContent = extra.text;
    if (window.console) console.error(msg);
    if ((kind === 'poisoned' || kind === 'short-end') && !reported[kind]) {
      reported[kind] = true;
      setTimeout(function () { throw new Error(msg); }, 0);
    }
  }

  /* ── Turning it off ────────────────────────────────────────────────────────────────────────────
     Any doubt at all and we stop, leaving the numbered pagination — which is always in the DOM — as
     the working path. A collection that paginates normally is a mild inconvenience; one that
     duplicates or skips products is a broken shop. */
  function disable(why) {
    disabled = true;
    if (observer) observer.disconnect();
    if (inFlight) inFlight.abort();
    inFlight = null;
    inFlightFor = null;
    rideActive = false;
    buffer = null;
    hideSpinner();
    if (moreBtn) moreBtn.hidden = true;
    /* Give the page links back — they are the only way forward now. */
    paginationHide(false);
    root.setAttribute('data-bc-disabled', why || '1');
  }

  function showSpinner() {
    /* Only after 150ms, and only when there is genuinely nothing buffered. In normal use the buffer is
       always warm, so this never appears — which is the point. */
    clearTimeout(spinnerTimer);
    spinnerTimer = setTimeout(function () {
      if (spinner) spinner.classList.add('is-on');
    }, 150);
  }

  function hideSpinner() {
    clearTimeout(spinnerTimer);
    if (spinner) spinner.classList.remove('is-on');
  }

  /* ── Fetch + parse, entirely off the insertion path ────────────────────────────────────────── */

  function fetchPage(url, attempt) {
    if (inFlight) inFlight.abort();
    var ctl = new AbortController();
    inFlight = ctl;
    inFlightFor = url;
    /* 20s, not 10s. Under Slow 4G the page's own below-fold images keep the pipe full long after
       first paint, and QA 2026-08-07 measured the 10s timer killing perfectly healthy prefetches —
       which is half of the auto-append stall documented at prefetch(). A page render is ~1.4s; if
       20s is not enough, the connection is bad enough that the recovery paths are the right answer.
       ⚠ abort(REASON): a timeout must be distinguishable from a supersede. fetch() rejects with the
       reason where supported, so a timeout surfaces as a plain Error ('bc-timeout') and takes the
       recovery paths, while a supersede stays an AbortError and stays silent. Engines that ignore
       the reason argument degrade to the old behaviour (timeout looks like a supersede). */
    /* 2026-08-28: the FIRST attempt gets 8 s, not 20. QA measured the first page-2 prefetch at
       29.6 s inside a real browser on live: `priority:'low'` behind lazysizes' post-load image flood
       (expand 1600 / expFactor 4 in theme.liquid). A render that has not answered in 8 s is either
       starved or dying, and either way a fresh `view=` render (attempt+1, priority 'auto') beats
       waiting. Retries and frugal links keep 20 s. */
    var limit = (!attempt && !isFrugal()) ? 8000 : 20000;
    var timer = setTimeout(function () {
      ctl.abort(new Error('bc-timeout'));
    }, limit);

    var opts = { signal: ctl.signal, credentials: 'same-origin' };
    /* Fetch Priority: a speculative page must never compete with the images the shopper is looking
       at. Ignored by browsers that do not support it, which is fine — it is a hint. */
    /* Low only while the shopper is still at the top and this is the speculative first attempt.
       Once they have scrolled, the next page IS the content they are looking at; a retry is never
       speculative. (`priority` is a hint; browsers without it ignore the property.) */
    try {
      opts.priority = (window.scrollY > 200 || attempt) ? 'auto' : 'low';
    } catch (e) {}

    return fetch(renderUrl(url, attempt), opts)
      .then(function (r) {
        clearTimeout(timer);
        if (r.status === 404 && gridSection && !gridBroken) {
          /* the grid section is not on this theme: use the page's own section from now on */
          gridBroken = true;
          if (window.console) console.warn('[brisk-inf] grid section 404 - falling back to ' + sectionId);
          if (inFlight === ctl) { inFlight = null; inFlightFor = null; }
          return fetchPage(url, attempt).then(function (res) { throw { bcPassthrough: res }; });
        }
        if (r.status === 429 && (attempt || 0) < MAX_POISON_RETRIES && !disabled && nextUrl === url) {
          /* Shopify rate-limits storefront section renders per client (QA 2026-08-28 saw 429s on
             pages 6 and 8 under fast scrolling). Not a poison: wait Retry-After (or 1.5 s) and
             re-issue, keeping ownership so the ride path does not compete. */
          var ra = parseFloat(r.headers.get('Retry-After')) || 1.5;
          retries++;
          if (window.console) console.warn('[brisk-inf] 429 page=' + (pageOf(url) || '?') + ' - retrying in ' + ra + 's');
          if (!inFlight) { inFlight = ctl; inFlightFor = url; }
          return new Promise(function (resolve) { setTimeout(resolve, Math.min(ra, 5) * 1000); }).then(function () {
            if (inFlight === ctl) { inFlight = null; inFlightFor = null; }
            if (disabled || nextUrl !== url) throw new Error('bc-stale');
            return fetchPage(url, (attempt || 0) + 1);
          }).then(function (res) { throw { bcPassthrough: res }; });
        }
        if (!r.ok) throw new Error('status ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (inFlight === ctl) { inFlight = null; inFlightFor = null; }
        var result = parse(sectionHtml(html), url);
        if (result.poisoned) {
          var pg = pageOf(url) || url;
          poisoned[pg] = (poisoned[pg] || 0) + 1;
          if ((attempt || 0) < MAX_POISON_RETRIES) {
            retries++;
            /* 🚨 STAY "IN FLIGHT" ACROSS THE BACK-OFF. `inFlight` was cleared above; if it stays
               null for the 400 ms wait, advance()'s ride sees "the prefetch failed" and starts a
               competing fetch of the SAME page (QA 2026-08-28: page 3 fetched 8 times). Re-own the
               handle with the spent controller — the retry's fetchPage replaces it. */
            if (!inFlight) { inFlight = ctl; inFlightFor = url; }
            /* back off a little, then ask for a FRESH render (new cache key) */
            return new Promise(function (resolve) {
              setTimeout(resolve, attempt ? 1200 : 400);
            }).then(function () {
              if (inFlight === ctl) { inFlight = null; inFlightFor = null; }
              if (disabled || nextUrl !== url) throw new Error('bc-stale');
              return fetchPage(url, (attempt || 0) + 1);
            });
          }
          if (window.console) console.warn('[brisk] page ' + pg + ' render poisoned x' + (MAX_POISON_RETRIES + 1) + ' - not appended');
          throw new Error('bc-poisoned');
        }
        return result;
      })
      ['catch'](function (err) {
        clearTimeout(timer);
        if (err && err.bcPassthrough) return err.bcPassthrough; /* grid-section 404 fallback result */
        /* 🚨 Only clear the shared handle if it is still OURS. A superseding fetchPage has already
           replaced `inFlight`; nulling that here would leave the live request un-abortable — the
           callers' catch blocks must never touch `inFlight` themselves for the same reason. */
        var mine = inFlight === ctl;
        if (mine) { inFlight = null; inFlightFor = null; }
        /* Our own timeout on a first attempt: re-issue at once under a fresh key with 'auto'
           priority, keeping ownership so the ride path does not start a competing fetch. A
           supersede (AbortError without our reason) stays silent as before. */
        var timedOut = ctl.signal.aborted && ctl.signal.reason && ctl.signal.reason.message === 'bc-timeout';
        if (timedOut && mine && (attempt || 0) < MAX_POISON_RETRIES && !disabled && nextUrl === url) {
          retries++;
          if (window.console) console.warn('[brisk-inf] timeout page=' + (pageOf(url) || '?') + ' after ' + limit + 'ms - retrying fresh');
          return fetchPage(url, (attempt || 0) + 1);
        }
        throw err;
      });
  }

  /* Parsing happens here, during idle time, so that insertion later is a single appendChild.
     Measured: ~5 ms for a page of 16 cards. */
  function parse(html, url) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var incomingGrid = doc.querySelector('[data-product-grid]');
    if (!incomingGrid) throw new Error('no grid in response');

    /* Sanity-check the server's page size against ours. If they disagree, `page=N` means different
       products to each side and we would duplicate or skip. See the note in main-collection.liquid. */
    var incomingRoot = doc.querySelector('[data-bc-inf]');
    if (incomingRoot && perPage) {
      var theirs = parseInt(incomingRoot.dataset.bcPerPage, 10);
      if (theirs && theirs !== perPage) {
        disable('per-page-mismatch');
        throw new Error('per-page mismatch: ' + theirs + ' vs ' + perPage);
      }
    }

    /* 🚨 REFUSE A POISONED PAGE (2026-08-28). Shopify kills a render that exceeds its budget and
       prints `Liquid error (…): internal` where the cards should be — HTTP 200, grid wrapper
       present, `[data-bc-inf]` present with a valid next url, ZERO cards. Until today this was
       appended as if it were a page: 16 shirts vanished silently and the raw error string was
       inserted into the grid. Three independent tells, any one is enough:
         1. a text node (not script/style) containing "Liquid error (";
         2. fewer `.product-block` than the server must have had for this page number
            (`min(perPage, total - (page-1)*perPage)`, from data-bc-total; ≥1 when total unknown);
         3. no `[data-bc-inf]` in the response although this is not the last page — the cursor
            would go blank while the numbered pagination is still hidden: a dead end. */
    var page = pageOf(url);
    var cards = incomingGrid.querySelectorAll('.product-block').length;
    var expected = 1;
    if (perPage && total && page) {
      expected = Math.min(perPage, total - (page - 1) * perPage);
      if (expected < 1) expected = 1;
    }
    var isLast = !!(perPage && total && page && page * perPage >= total);
    var poison = false;
    var walker = doc.createTreeWalker(incomingGrid, NodeFilter.SHOW_TEXT, null);
    var tn;
    while ((tn = walker.nextNode())) {
      var pt = tn.parentNode && tn.parentNode.nodeName;
      if (pt === 'SCRIPT' || pt === 'STYLE') continue;
      if (tn.nodeValue && tn.nodeValue.indexOf('Liquid error (') !== -1) { poison = true; break; }
    }
    if (!poison && cards < expected) poison = true;
    /* Tell 3 needs a KNOWN total: on /search `data-bc-total` is capture-guarded and can be absent,
       and with total=0 `isLast` is never true — the legitimate last page (no next, so no
       `[data-bc-inf]`) would be refused as poisoned (independent review, 2026-08-28). */
    if (!poison && !incomingRoot && total && !isLast) poison = true;
    if (poison) return { poisoned: true, url: url, cards: cards, expected: expected };

    /* ⚠ EVERY child moves, not just `.product-block`. Measured: a 16-product page renders 32 children
       — sixteen cards, each PRECEDED BY ITS OWN INLINE `<style>` element. Filtering to `.product-block`
       would leave those styles behind and appended cards would lose per-card styling. The `<style>`
       nodes are `display: none`, so they take no grid cell. */
    var frag = document.createDocumentFragment();
    var cards = incomingGrid.children;
    while (cards.length) {
      var el = cards[0];
      /* 🚨 Strip the server's eager hints BEFORE this ever enters a live document. Correct for a page
         view, wrong for an append: these cards are far below the fold. Safe to do here only because a
         DOMParser document has no browsing context and has therefore started no requests. */
      if (el.querySelectorAll) {
        var imgs = el.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
          imgs[i].removeAttribute('fetchpriority');
          imgs[i].setAttribute('loading', 'lazy');
        }
      }
      frag.appendChild(el); /* moves it out of `cards`, so this loop terminates */
    }

    return {
      frag: frag,
      next: incomingRoot ? cleanNext(incomingRoot.dataset.bcNext) : '',
      count: frag.childElementCount,
      url: url
    };
  }

  /* ── The buffer: always try to be one page ahead ───────────────────────────────────────────── */

  function prefetch() {
    if (disabled || suspended || !nextUrl || buffer || inFlight || isFrugal()) return;
    var want = nextUrl;
    if (!firstPrefetchAt) firstPrefetchAt = Date.now();
    fetchPage(want)
      .then(function (result) {
        if (!firstPrefetchMs) firstPrefetchMs = Date.now() - firstPrefetchAt;
        /* `nextUrl` may have moved on (a filter change) while this was in flight. Keying the buffer to
           the url it was fetched for means a stale result is simply never used. */
        buffer = result;
        bufferedFor = want;
      })
      .catch(function () {
        /* 🚨 A failed prefetch must NOT stay silent when the shopper is already waiting at the end.
           QA 2026-08-07 reproduced this twice on Slow 4G: the prefetch died on its abort timer while
           the shopper sat inside the sentinel's 800px rootMargin — and IntersectionObserver only
           fires on CROSSINGS, so with no re-entry there was no retry, no spinner, no button. The
           page just stopped until they scrolled up a full screen and back.
           So: superseded (a newer fetch owns `inFlight`), riding in progress, or the cursor moved
           on → still silent, exactly as before. But if the sentinel is at or near the viewport,
           hand the failure to advance()'s on-demand path — it shows the spinner, retries once, and
           on a second failure surfaces Load more / pagination. Bounded, no loop: advance()'s own
           catch never calls prefetch(). */
        if (disabled || suspended || rideActive || inFlight || nextUrl !== want || !sentinel) return;
        var r = sentinel.getBoundingClientRect();
        if (r.top < window.innerHeight + 800) advance(false);
      });
  }

  /* ── Insertion ─────────────────────────────────────────────────────────────────────────────── */

  function insert(result) {
    var page = pageOf(result.url);
    if (page) {
      if (seen[page]) return false;
      seen[page] = true;
    }

    /* Last guard before the DOM: drop any card whose product id is already in the grid (and the
       inline <style> that precedes it). `seen[page]` cannot catch a product added or removed
       between two renders shifting the page boundary by one. ~16 lookups, no layout. */
    var have = {};
    var existing = grid.querySelectorAll('.product-block[data-product-id]');
    for (var e = 0; e < existing.length; e++) have[existing[e].getAttribute('data-product-id')] = 1;
    var dupes = result.frag.querySelectorAll('.product-block[data-product-id]');
    var dropped = 0;
    for (var d = 0; d < dupes.length; d++) {
      var pid = dupes[d].getAttribute('data-product-id');
      if (have[pid]) {
        var prev = dupes[d].previousElementSibling;
        if (prev && prev.nodeName === 'STYLE') prev.parentNode.removeChild(prev);
        dupes[d].parentNode.removeChild(dupes[d]);
        dropped++;
      }
    }
    if (dropped && window.console) console.warn('[brisk-inf] dropped ' + dropped + ' duplicate card(s) on page ' + page);

    grid.appendChild(result.frag); /* one node insertion, one layout pass */
    nextUrl = result.next;

    /* 🚨 NEVER A SILENT END (2026-08-28). If the server says there is no next page but the grid
       holds fewer cards than the collection has, something upstream lied (a poisoned last page,
       a cursor echo, a per-page drift). Keep a cursor to the page after this one, hand the shopper
       the button, and make it visible in RUM. */
    var haveNow = grid.querySelectorAll('.product-block').length;
    if (!nextUrl && total && page && haveNow < total) {
      try {
        var nu = new URL(result.url, window.location.origin);
        nu.searchParams.set('page', String(page + 1));
        nextUrl = nu.pathname + nu.search;
      } catch (e2) { nextUrl = ''; }
      mark('short-end', { page: page, text: 'Some shirts did not load. Tap Load more to try again.' });
      if (moreBtn && nextUrl) { moreBtn.hidden = false; } else { paginationHide(false); }
    }

    /* 🚨 WRITE IT BACK TO THE DOM TOO. The attribute — not the variable — is the source of truth,
       because reset() re-reads it after a filter change. Updating only the variable is what caused
       "it renders the same products every time": the attribute stayed on page 2 for ever, so the
       moment anything called reset() the cursor snapped back to page 2 and stayed there. */
    root.dataset.bcNext = nextUrl;

    /* 🚨 RE-ARM THE OBSERVER AFTER EVERY APPEND. IntersectionObserver reports CROSSINGS, and an
       appended page can be SHORTER than the trigger zone (viewport + 800px rootMargin). When it is,
       the sentinel never leaves the zone, `isIntersecting` never changes, no record is ever
       delivered again, and the page stops silently — with the numbered pagination already hidden.
       Found 2026-08-07: at 795x997 (the 768-999px band is 4 columns of SHORT cards on a TALL
       viewport) a 16-product append is ~1,450px against a ~1,797px zone, so page 3 never loaded.
       unobserve+observe forces a fresh initial record for the sentinel's CURRENT state; if it is
       still inside the zone, advance() runs again — bounded, as ever, by the autoPages budget. */
    if (observer && sentinel && result.next) {
      observer.unobserve(sentinel);
      observer.observe(sentinel);
    }

    /* Re-init is deferred a tick so the frame that inserted stays cheap. Order mirrors theme.js's own
       ajaxLoadUrl: images first, then scroll animations.

       🚨 DO NOT TRIGGER `cc:component:load` HERE. That was in the first version and it was a
       self-inflicted infinite loop: the container's components are `accordion,modal,price-range` —
       the FILTER accordions, a modal and the price slider — and not one of them exists inside a
       product card, so re-initialising them after appending products achieves nothing. Worse, this
       file listens for that same event to detect a filter re-render, so firing it here called our own
       reset(), which reset the pagination cursor and cleared the duplicate guard. Page 2 was then
       appended for ever.
       Appended cards need exactly two things, both below. Everything else about a product card is
       bound by ProductBlockManager (hover images, swatches) or by theme.js handlers delegated on
       `document`, which appended nodes get for free. Quickbuy builds its container on demand. */
    idle(function () {
      try {
        if (window.theme && theme.ProductBlockManager) theme.ProductBlockManager.loadImages(grid);
        if (window.theme && theme.initAnimateOnScroll) theme.initAnimateOnScroll();
      } catch (e) {
        /* Re-init failing must not take the page down; the cards are already in and clickable. */
      }
    });

    if (status && outcome !== 'short-end') status.textContent = result.count + ' more products loaded';
    labelButton();
    return true;
  }

  /* Called when the sentinel comes into view, or when the button is clicked. */
  function advance(fromButton) {
    if (disabled || !nextUrl) return;

    if (!fromButton) {
      if (autoUsed >= autoPages) {
        /* Hand control back to the shopper: keeps the DOM bounded and the footer reachable.
           2026-08-28: the observer STAYS attached (crossings are ignored while `paused`) and the
           next page is buffered behind the button, so the click is one appendChild. */
        if (moreBtn) { moreBtn.hidden = false; labelButton(); }
        paused = true;
        prefetch();
        return;
      }
      /* ⚠ The budget is spent by SUCCESSFUL INSERTS, not by intersections. It used to be
         `autoUsed++` right here — before any work — so a duplicate-page no-op, a stood-down ride
         or a failed fetch all burned budget, and the Load-more hand-off arrived pages early
         (QA 2026-08-07). Every insert site below does `spendAuto()` on a true return instead. */
    }
    function spendAuto() {
      if (!fromButton) autoUsed++;
    }

    /* The fast path: already buffered. Consume it by nulling it FIRST, so no second caller can insert
       the same fragment. */
    if (buffer && bufferedFor === nextUrl) {
      var ready = buffer;
      buffer = null;
      bufferedFor = null;
      if (insert(ready)) spendAuto();
      prefetch(); /* immediately start on the page after this one */
      return;
    }

    /* The slow path: not buffered (first-ever call on a frugal connection, or a prefetch that failed
       or is still in flight). This is the only path where the shopper can see the indicator. */
    showSpinner();
    var want = nextUrl;

    /* 🚨 RIDE AN IN-FLIGHT PREFETCH OF THIS EXACT PAGE INSTEAD OF RESTARTING IT. fetchPage() aborts
       whatever is in flight, so calling it here while the prefetch for `want` is mid-transfer threw
       away the bytes already received and made Shopify render the identical page AGAIN — QA
       2026-08-07 logged pages 3 and 4 each rendered twice under fast scrolling, and on a slow link
       the restart lands exactly when bandwidth is scarcest. Poll the buffer the prefetch will fill:
       its .then() sets `buffer` synchronously on resolve, so 100ms granularity is plenty. */
    if (inFlight && inFlightFor === want) {
      rideActive = true;
      var rideStart = Date.now();
      (function ride() {
        if (disabled || nextUrl !== want) { rideActive = false; hideSpinner(); return; }
        if (buffer && bufferedFor === want) {
          rideActive = false;
          hideSpinner();
          var ready = buffer;
          buffer = null;
          bufferedFor = null;
          if (insert(ready)) spendAuto();
          prefetch();
          return;
        }
        if (!inFlight) {
          /* the ridden prefetch failed — take the normal fetch path (its catch handles dead ends).
             prefetch()'s own failure fallback stands down while rideActive is set, so this is the
             only recovery that runs. */
          rideActive = false;
          fetchAndInsert(want, spendAuto);
          return;
        }
        if (Date.now() - rideStart > 30000) {
          /* The ride cap used to stand down silently — no spinner, no button, and with the
             numbered pagination still hidden the shopper had no way forward unless they happened
             to re-cross the sentinel (QA 2026-08-07, finding L1: the exact dead-end shape the
             prefetch-failure fallback was built to prevent). In practice fetchPage's own 20s abort
             fires first and the `!inFlight` branch above recovers, so this cap only triggers if
             that timer somehow never runs — and then it must fail the same way fetchAndInsert's
             catch does: surface the button (or hand back pagination) and stop observing. The
             still-in-flight fetch is left to its owner — if it ever resolves it fills the buffer,
             and the button click consumes it instantly via the fast path. */
          rideActive = false;
          hideSpinner();
          if (moreBtn) {
            moreBtn.hidden = false;
          } else {
            paginationHide(false);
          }
          if (observer) observer.disconnect();
          return;
        }
        setTimeout(ride, 100);
      })();
      return;
    }

    fetchAndInsert(want, spendAuto);
  }

  /* `spend` is advance()'s spendAuto closure — called only when the insert actually landed, so the
     auto-page budget tracks pages the shopper received rather than attempts. Button-initiated calls
     pass it too; it no-ops there (fromButton). */
  function fetchAndInsert(want, spend) {
    fetchPage(want)
      .then(function (result) {
        hideSpinner();
        if (insert(result) && spend) spend();
        prefetch();
      })
      .catch(function (err) {
        hideSpinner();
        /* `inFlight` is cleaned inside fetchPage, and only by its owner — never here. */
        if (err && err.name === 'AbortError') return; /* superseded, not a failure */
        if (err && err.message === 'bc-stale') return; /* filter changed under a retry */
        if (err && err.message === 'bc-poisoned') {
          mark('poisoned', { page: pageOf(want), text: 'Some shirts did not load. Tap Load more to try again.' });
        } else if (err && err.message !== 'bc-stale') {
          mark('fetch-failed', { page: pageOf(want), text: 'Some shirts did not load. Tap Load more to try again.' });
        }
        /* Never a dead end: offer the button so the shopper can retry deliberately, and if there is
           no button, hand the numbered pagination back instead. */
        if (moreBtn) {
          moreBtn.hidden = false;
        } else {
          paginationHide(false);
        }
        if (observer) observer.disconnect();
      });
  }

  /* ── Reset after theme.js re-renders the grid ──────────────────────────────────────────────────
     A filter or sort change replaces the contents of every `[data-ajax-container]`, which includes the
     grid and this whole block. Everything we know is then stale.
     🚨 jQuery-bound because theme.js fires this with `.trigger()`; addEventListener never sees it. */
  function reset() {
    suspended = false;
    if (inFlight) inFlight.abort();
    inFlight = null;
    inFlightFor = null;
    rideActive = false;
    buffer = null;
    bufferedFor = null;
    seen = {};
    poisoned = {};
    paused = false;
    outcome = '';
    autoUsed = 0;
    disabled = false;
    hideSpinner();

    /* Re-resolve every node: the old ones were thrown away with the replaced HTML. */
    root = document.querySelector('[data-bc-inf]');
    grid = document.querySelector('[data-product-grid]');
    if (!root || !grid) {
      if (observer) observer.disconnect();
      return;
    }
    sentinel = root.querySelector('[data-bc-sentinel]');
    spinner = root.querySelector('[data-bc-spinner]');
    moreBtn = root.querySelector('[data-bc-loadmore]');
    status = root.querySelector('[data-bc-status]');
    nextUrl = root.dataset.bcNext || '';
    total = parseInt(root.dataset.bcTotal, 10) || 0;
    bindButton();
    observe();
    paginationHide(sentinel && moreBtn && nextUrl);
    idle(prefetch);
  }

  /* ── Wiring ────────────────────────────────────────────────────────────────────────────────── */

  /* ⚠ IntersectionObserver DOES NOT FIRE IN A BACKGROUND TAB, and neither does requestAnimationFrame —
     Chrome stops giving hidden documents rendering opportunities. That is correct behaviour (a tab
     nobody is looking at needs no products) but it makes this untestable from an automation harness
     whose window is minimised: verified during development, where every observer on every element
     returned nothing while `document.visibilityState` was "hidden". If this ever appears not to fire,
     check that first before suspecting the logic. */
  function observe() {
    if (observer) observer.disconnect();
    if (!sentinel || !nextUrl) return;
    /* 800px of lead so insertion happens before the shopper reaches the end even when the buffer had
       to be fetched on demand. */
    observer = new IntersectionObserver(
      function (entries) {
        if (paused) return;
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            advance(false);
            return;
          }
        }
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(sentinel);
  }

  function bindButton() {
    if (!moreBtn) return;
    /* reset() re-runs the wiring, and on an unchanged node that would stack a second click handler —
       two appends per click. The flag lives on the element so a replaced node gets a fresh binding. */
    if (moreBtn.dataset.bcBound) return;
    moreBtn.dataset.bcBound = '1';
    moreBtn.addEventListener('click', function () {
      moreBtn.hidden = true;
      paused = false;
      autoUsed = 0; /* the click buys another full run of automatic pages */
      if (status && outcome) { status.textContent = ''; }
      advance(true);
      observe();
    });
  }

  /* "Load more (N remaining)" when the total is known — the button says how much is left. */
  function labelButton() {
    if (!moreBtn || !total) return;
    var have = grid ? grid.querySelectorAll('.product-block').length : 0;
    var left = total - have;
    var base = moreBtn.getAttribute('data-bc-label') || moreBtn.textContent.replace(/\s*\(.*$/, '').trim();
    moreBtn.setAttribute('data-bc-label', base);
    moreBtn.textContent = left > 0 ? base + ' (' + left + ' remaining)' : base;
  }

  /* ── Start-up ─────────────────────────────────────────────────────────────────────────────────
     Nothing above ran any work. Everything below is gated so that this file cannot cost the initial
     page load anything measurable:

     🚨 THE FIRST PREFETCH WAITS FOR `load`, NOT JUST FOR IDLE. It is ~1.2 s of Shopify render and a
     13 KB response, and `requestIdleCallback` can fire while the page is still fetching its own
     below-fold card images — on a slow connection that would be this feature stealing bandwidth from
     the page the shopper is actually looking at. The previous implementation on this theme was
     downloaded on EVERY page of the site and did nothing; the merchant remembers it as "it used to not
     load and reduce page speed", so the bar here is that infinite scroll costs nothing until it is
     needed. Waiting for `load` gives up nothing real: the shopper has to scroll more than a screen
     before the buffer matters.
     Observing starts at idle rather than at `load`, so the sentinel is live immediately for anyone who
     scrolls fast — if they beat the buffer they simply get the on-demand path, which still works. */
  /* 🚨 THE PAGINATION IS ONLY HIDDEN IF THERE IS SOMETHING TO REPLACE IT WITH.
     Hiding it unconditionally shipped a dead end: the merchant's browser had the new JS with the old
     Liquid — Shopify syncs assets before sections — so `moreBtn` was null, no Load more button existed,
     and the page links were hidden anyway. The grid simply stopped with no way forward.
     So the hide now REQUIRES all three of the things that make infinite scroll work to be present, and
     disable() takes it back off. A shopper must never be able to reach the end of a grid with no way to
     continue. */
  function paginationHide(on) {
    document.documentElement.classList.toggle('bc-inf-on', !!on);
  }

  idle(function () {
    bindButton();
    observe();
    paginationHide(sentinel && moreBtn && nextUrl);
  });

  /* 2026-08-28: the first prefetch no longer waits for `load`. On live, `load` can be 30 s+ away
     and the moment it fires lazysizes raises its expand to 1600px and floods the queue — the
     page-2 fetch, queued right then at low priority, measured 29.6 s. Now: idle after
     DOMContentLoaded (2.5 s ceiling), or the shopper's first scroll, whichever comes first. The
     LCP image is `fetchpriority="high"` and already in flight from HTML; one low-priority 224 KB
     fetch started after DOMContentLoaded cannot pre-empt it, and parsing still happens in idle. */
  var kicked = false;
  function kick() {
    if (kicked) return;
    kicked = true;
    idle(prefetch, { timeout: 2500 });
  }
  if (document.readyState !== 'loading') {
    kick();
  } else {
    document.addEventListener('DOMContentLoaded', kick, { once: true });
  }
  window.addEventListener('scroll', kick, { once: true, passive: true });

  /* bfcache / back navigation: a persisted page keeps this closure but any in-flight request and
     its timer are stale, and Chrome's scroll restoration can land the viewport past the sentinel
     with no crossing to observe. Re-arm: a fresh IntersectionObserver delivers an initial record
     for the sentinel's CURRENT position, so one already inside the zone fires immediately. */
  window.addEventListener('pageshow', function (ev) {
    if (!ev.persisted) return;
    hideSpinner();
    rideActive = false;
    if (inFlight) { try { inFlight.abort(); } catch (e) {} }
    inFlight = null;
    inFlightFor = null;
    observe();
    if (nextUrl && !buffer) idle(prefetch);
  });

  /* ── Detecting a filter or sort change ────────────────────────────────────────────────────────
     theme.js replaces the contents of every `[data-ajax-container]`, which includes this whole block,
     then fires `cc:component:load` — with jQuery `.trigger()`, so this must stay jQuery-bound; a
     native addEventListener cannot hear it.

     🚨 THE TEST IS NODE IDENTITY, NOT THE EVENT ITSELF. `cc:component:load` fires on first load, for
     every component, and from anything else on the page that re-initialises a component. Resetting on
     the event alone reset us constantly. A real re-render REPLACES the element, so the node in the
     document is no longer the node we hold — and that is unambiguous, whatever fired the event. */
  /* ── A tiny state introspector ─────────────────────────────────────────────────────────────────
     Deliberately left in. This feature has a lot of moving parts that are invisible from the outside —
     a closure variable being empty looks identical to an event never firing, and diagnosing "Load more
     does nothing" from the DOM alone cost real time. Reading it is free; nothing here allocates or
     observes. Call `window.__briskInfinite()` in the console. */
  window.__briskInfinite = function () {
    return {
      nextUrl: nextUrl,
      buffered: !!buffer,
      bufferedFor: bufferedFor,
      inFlight: !!inFlight,
      inFlightFor: inFlightFor,
      rideActive: rideActive,
      autoUsed: autoUsed,
      autoPages: autoPages,
      seen: Object.keys(seen),
      disabled: disabled,
      observing: !!observer,
      perPage: perPage,
      sectionId: sectionId,
      gridSection: gridBroken ? '' : gridSection,
      rootIsCurrent: root === document.querySelector('[data-bc-inf]'),
      frugal: isFrugal(),
      total: total,
      poisoned: poisoned,
      retries: retries,
      paused: paused,
      outcome: outcome,
      firstPrefetchMs: firstPrefetchMs
    };
  };

  if (window.jQuery) {
    window.jQuery(document).on('cc:component:load', function () {
      var current = document.querySelector('[data-bc-inf]');
      if (current !== root) reset();
    });
  }

  /* Free the pipe the moment a filter/sort input changes — capture phase, so this runs regardless
     of theme.js's own delegated handlers. The buffer is cleared too: it holds a page of the OLD
     filter state, and reset() (which would clear it anyway) only fires after the swap. */
  document.addEventListener(
    'change',
    function (e) {
      var t = e.target;
      if (!t || !t.form || t.form.id !== 'CollectionFilterForm') return;
      suspended = true;
      buffer = null;
      bufferedFor = null;
      if (inFlight) {
        try { inFlight.abort(); } catch (err) { /* owner cleans up */ }
      }
    },
    true
  );
})();
